import {
  Formik,
  type FormikErrors,
  Form as FormikForm,
  type FormikHelpers,
  type FormikProps,
  type FormikValues,
  useFormikContext,
} from 'formik';
import type React from 'react';
import { useEffect } from 'react';
import type { ObjectSchema } from 'yup';
import type { InputSizes } from './InputText';

interface FieldConfig {
  type?:
    | 'text'
    | 'autocomplete'
    | 'select'
    | 'radio'
    | 'checkbox'
    | 'email'
    | 'password';
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export interface FormProps<T> {
  title?: string;
  size?: InputSizes;
  subtitle?: string;
  direction?: 'vertical' | 'horizontal';
  noLabels?: boolean;
  fieldsConfig?: FieldConfig[];
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => void;
  enableReinitialize?: boolean;
  validationSchema?: ObjectSchema<any>;
  initialValues: T;
  initialStatus?: string;
  initialTouched?: any;
  children?: React.ReactNode | React.ReactNode[];
  onError?: (errors: FormikErrors<T>) => void;
  onValueChange?: (values: T) => void;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  submitLabel?: string;
  resetOnSubmit?: boolean;
  className?: string;
  innerRef?: React.Ref<FormikProps<any>>;
}

interface FormikContext<T> {
  values: T;
  errors: FormikErrors<T>;
}

/**
 * @deprecated This component should not be used.
 * It introduces many constraints on top of the Formik component from the Formik library, while adding very little value.
 * Instead, use the Formik component directly from Formik library.
 */
export const Form = <T,>({
  children,
  onSubmit,
  initialValues,
  initialStatus,
  validationSchema,
  enableReinitialize,
  validateOnBlur,
  initialTouched,
  validateOnChange,
  onError,
  onValueChange,
  resetOnSubmit,
  className,
  innerRef,
}: FormProps<T>) => {
  const FormikContextPropagator = () => {
    const { values, errors }: FormikContext<T> = useFormikContext();

    useEffect(() => {
      if (onValueChange && values) {
        onValueChange(values);
      }
    }, [values]);

    useEffect(() => {
      if (onError && errors) {
        onError(errors);
      }
    }, [errors]);

    return null;
  };

  return (
    <Formik
      innerRef={innerRef}
      validateOnMount={false}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      onSubmit={(values, formikHelpers) => {
        if (resetOnSubmit) {
          formikHelpers.resetForm();
        }
        onSubmit(values as T, formikHelpers);
      }}
      initialTouched={initialTouched}
      initialStatus={initialStatus}
      enableReinitialize={enableReinitialize}
      validationSchema={validationSchema}
      initialValues={initialValues as FormikValues}
    >
      {() => {
        return (
          <FormikForm className={className}>
            {children}
            <FormikContextPropagator />
          </FormikForm>
        );
      }}
    </Formik>
  );
};
